import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService, CrudService, RxjsService } from '@app/shared/services';
import {
  IApplicationResponse, SidebarDataLoaded, SidebarDataRemoved,
  ModulesBasedApiSuffix, AuthenticationModuleApiSuffixModels, SharedModuleApiSuffixModels, agentLoginDataSelector,
  prepareRequiredHttpParams, DEFAULT_COMMON_DASHBOARD_NAVIGATION, LoggedInUserModel, retrieveAlreadyLoggedInAgentDetails,
} from '@app/shared';
import { Login, Logout } from '../auth.actions';
import { UserModuleApiSuffixModels, StaticEagerLoadingCreate, DynamicEagerLoadingCreate } from '@app/modules';
import { forkJoin, of } from 'rxjs';
import { HubConnection } from '@microsoft/signalr';
import { MatDialog } from '@angular/material';
import { Store, select } from '@ngrx/store';
import { AppState } from '@app/reducers';
import { loggedInUserData } from '@modules/others';
import { SalesModuleApiSuffixModels } from '@modules/sales';
import { JobSelectionScanModalComponent } from '@modules/inventory/components/instaff-job-selection/job-selection-list/job-selection-scan-modal.component';
import { combineLatest } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { CryptoUtils } from 'msal/lib-commonjs/utils/CryptoUtils';
import { MsalService } from '@azure/msal-angular';
import { MessageService } from 'primeng/api';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AesGcmCryptoService } from '../../../shared/services/aes-gcm-crypro.service';
import { AgentLoginService } from '../../../shared/services/agent-login.service';
@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  providers: [MessageService]
})

export class LoginComponent implements OnInit {
  loginForm: FormGroup;
  isFormSubmitted = false;
  loginError: boolean = false;
  applicationResponse: IApplicationResponse;
  connection: HubConnection;
  agentExtensionNo;
  loggedInUserData = new LoggedInUserModel();
  isUserTypeSelectedAsExternal = null;
  isLoggedOut = false;
  sound: any;
  msgs: any = [];
  isBtnDisabled = false;
  remainingAttempts = 0;

  constructor(
    private formBuilder: FormBuilder, private router: Router, private deviceService: DeviceDetectorService,
    private userService: UserService, private activatedRoute: ActivatedRoute, private dialog: MatDialog,
    private crudService: CrudService, private rxjsService: RxjsService,
    private store: Store<AppState>, private readonly agentLoginService: AgentLoginService,
    private msalService: MsalService, private aesGcmCryptoService: AesGcmCryptoService) {
    this.rxjsService.setDialogOpenProperty(true);
    this.dialog.closeAll();
    this.msgs = [];
  }

  ngOnInit() {
    this.rxjsService.setGlobalLoaderProperty(false);
    this.combineLatestNgrxStore();
    this.connection?.stop();
    this.sound = document.getElementById("audio");
    this.createLoginFormGroup();

    // Handle MSAL redirect callback — processes the token when Microsoft redirects back
    this.msalService.handleRedirectCallback((authError, response) => {
      if (response) {
        this.isUserTypeSelectedAsExternal = false;
        this.MFAConfigInitialization(response);
      }

      if (authError) {
        console.error("MSAL redirect error:", authError);
      }
    });

    this.activatedRoute.queryParams.pipe(take(1)).subscribe(({ isLoggedOut }: any) => {
      this.isLoggedOut = isLoggedOut == 'true' ? true : false;
      if (this.isLoggedOut) {
        this.isUserTypeSelectedAsExternal = false;
        this.userService.onAppLogout(sessionStorage.getItem('userId'));
      }
      this.userService.removeAllStorageDataAndAppData('login');
    });
    this.rxjsService.getLogoutResponse().subscribe((isResponseReceived: boolean) => {
      if (isResponseReceived) {
        this.isUserTypeSelectedAsExternal = null;
      }
    });
    this.onFormControlChanges();
  }

  onFormControlChanges() {
    this.loginForm.get('password').valueChanges.subscribe(() => {
      if (this.remainingAttempts === 1) {
        this.loginForm.get('password').setErrors({ accountDisabled: true });
      }
    });
  }

  combineLatestNgrxStore() {
    combineLatest([
      this.store.pipe(select(agentLoginDataSelector)),
      this.store.select(loggedInUserData)
    ]).subscribe((response) => {
      this.agentExtensionNo = response[0];
      this.loggedInUserData = new LoggedInUserModel(response[1]);
    });
  }

  getUpdatedNotifications(loggedInUserId: string) {
    this.crudService.get(ModulesBasedApiSuffix.COMMON_API, SharedModuleApiSuffixModels.NOTIFICATIONS, loggedInUserId)
      .subscribe((response: IApplicationResponse) => {
        if (response.isSuccess && response.statusCode == 200 && response.resources) {
          this.rxjsService.setNotifications({ notifications: response.resources.notifications, count: response.resources.notificationCount });
        }
        this.rxjsService.setGlobalLoaderProperty(false);
      });
  }

  getTAMFollowUpNotification() {
    this.crudService.get(ModulesBasedApiSuffix.TECHNICIAN,
      SharedModuleApiSuffixModels.TAM_INVESTIGATION_FOLLOWUP_NOTIFICATION)
      .subscribe((response: IApplicationResponse) => {
        if (response.isSuccess && response.statusCode == 200 && response.resources.length > 0) {
          let notificationMessage = response.resources[0].notificationMessage;
          const dialogCode = this.dialog.open(JobSelectionScanModalComponent, {
            width: '450px',
            data: {
              header: `TAM Investigation`,
              message: notificationMessage,
              buttons: {
                create: 'Ok'
              }
            }, disableClose: true
          });
          dialogCode.afterClosed().subscribe(result => {
            if (!result) return;
            this.rxjsService.setDialogOpenProperty(false);
          });
        }
      });
  }

  createLoginFormGroup(): void {
    this.loginForm = this.formBuilder.group({
      username: ["", [Validators.required, Validators.email]],
      password: ["", [Validators.required]]
    });
  }

  getForkJoinRequests(): void {
    forkJoin([
      this.crudService.dropdown(ModulesBasedApiSuffix.SALES, SalesModuleApiSuffixModels.SALES_API_UX_STATIC, undefined, 1).pipe(map(result => result), catchError(error => of(error))),
      this.crudService.dropdown(ModulesBasedApiSuffix.SALES, SalesModuleApiSuffixModels.SALES_API_UX_DYNAMIC, undefined, 1)]).pipe(map(result => result), catchError(error => of(error)))
      .subscribe((response: IApplicationResponse[]) => {
        response.forEach((respObj: IApplicationResponse, ix: number) => {
          if (respObj?.isSuccess && respObj?.statusCode == 200) {
            switch (ix) {
              case 0:
                this.store.dispatch(new StaticEagerLoadingCreate({ staticEagerLoadingData: respObj.resources }));
                break;
              case 1:
                this.store.dispatch(new DynamicEagerLoadingCreate({ dynamicEagerLoadingData: respObj.resources }));
                break;
            }
          }
        });
      });
  }

  onUserTypeSelected(type: string) {
    if (type == 'internal') {
      this.MFALogin();
    }
    else if (type == 'external') {
      this.isUserTypeSelectedAsExternal = true;
    }
  }

  MFALogin() {
    // Redirect the entire page to Microsoft login. When the user authenticates,
    // Microsoft redirects back to our login page and handleRedirectCallback in ngOnInit processes the response.
    // this.msalService.loginRedirect();
    this.msalService.loginRedirect({
      redirectUri: window.location.origin + '/login'
    });

  }

  MFAConfigInitialization(response) {
    if (!response || !response.idToken?.rawIdToken) {
      console.error("No ID token received from Azure AD");
      return;
    }

    const idToken = response.idToken?.rawIdToken;

    // Send ID token to your backend (same as before)
    this.retrieveAccessTokenFromMSAL(idToken);
  }

  retrieveAccessTokenFromMSAL(encodedToken: string) {
    this.aesGcmCryptoService.encrypt(encodedToken).then((encryptedEncodedToken: string) => {
      this.rxjsService.setAnyPropertyValue(encryptedEncodedToken);
      // Send the new request along with the above encoded Token to retrieve the actual user information to access the app further
      this.crudService.create(ModulesBasedApiSuffix.AUTHENTICATION, AuthenticationModuleApiSuffixModels.AD_LOGIN)
        .subscribe((response: IApplicationResponse) => {
          this.rxjsService.setAnyPropertyValue(null);
          if (response.message == 'Invalid User') {
            this.isUserTypeSelectedAsExternal = null;
            this.router.navigateByUrl("/login");

          } else if (response.statusCode == 409) {
            this.isUserTypeSelectedAsExternal = null;
          }
          else if (response?.resources?.accessToken) {
            this.onSubmitSuccessLogic(response);
          }
        }, error => {
          this.onAfterSubmit();
        });
    });
  }

  // For mozilla firefox password masking
  getPasswordType() {
    return window.navigator?.userAgent?.indexOf('Firefox') != -1 ? 'password' : 'text';
  }

  hubConnectionForSalesAPIConfigs(loggedInUserId?: string) {
    this.rxjsService.setLoginSuccess(true);
  }

  onSubmit(): void {
    this.isFormSubmitted = true;
    if (this.remainingAttempts === 1) {
      this.loginForm.get('password').setErrors(null);
      this.loginForm.updateValueAndValidity();
    }
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      this.isFormSubmitted = false;
      return;
    }
    this.isBtnDisabled = true;
    this.rxjsService.setFormChangeDetectionProperty(true);
    let reqObj = { ...this.loginForm.value };
    this.aesGcmCryptoService.encrypt(reqObj?.username)?.then((val) => {
      reqObj['username'] = val;
    });
    this.aesGcmCryptoService.encrypt(reqObj?.password)?.then((val) => {
      reqObj['password'] = val;
      this.onAfterSubmitButton(reqObj);
    });
  }

  onAfterSubmitButton(reqObj) {
    this.crudService.create(ModulesBasedApiSuffix.AUTHENTICATION, AuthenticationModuleApiSuffixModels.LOGIN_V2, reqObj)
      .subscribe((response: IApplicationResponse) => {
        if (response?.isSuccess && response?.statusCode == 200) {
          let redirectRouteUrl = "";
          let parsedUserData: LoggedInUserModel;
          this.aesGcmCryptoService.decrypt(response.resources['accessToken']).then((decryptedEncodedToken) => {
            const decodedUserData = this.userService.getDecodedLoggedInData(decryptedEncodedToken);
            if (decodedUserData) {
              redirectRouteUrl = DEFAULT_COMMON_DASHBOARD_NAVIGATION;
              parsedUserData = JSON.parse(decodedUserData['userData']);
              this.prepareGlobalDateFormat(parsedUserData);
              this.store.dispatch(new Login({
                user: parsedUserData, encodedToken: response.resources['accessToken'],
                encryptedUserId: "", defaultRedirectRouteUrl: redirectRouteUrl,
                refreshToken: response.resources['refreshToken']
              }));
              this.aesGcmCryptoService.encrypt(parsedUserData.userId).then((encryptedUserId: string) => {
                this.store.dispatch(new Login({
                  user: parsedUserData, encodedToken: response.resources['accessToken'],
                  encryptedUserId, defaultRedirectRouteUrl: redirectRouteUrl,
                  refreshToken: response.resources['refreshToken']
                }));
              });
              setTimeout(() => {
                this.onCheckWhetherUserLoggedInAnotherDevice(parsedUserData.userId);
                /* If the agent is already logged In the same system and the same user ID ( event with different browsers ) then call the api to fetch the 
      agent details rather than logging in again */
                if (parsedUserData?.mediaTypeIds?.length > 0 && this.agentLoginService.isOpenScapeProvider) {
                  retrieveAlreadyLoggedInAgentDetails(this.crudService, this.userService, this.loggedInUserData, "", this.store);
                }
                this.crudService.get(ModulesBasedApiSuffix.IT_MANAGEMENT, UserModuleApiSuffixModels.MENU_ROLE_V1,
                  undefined, false, prepareRequiredHttpParams({ userId: parsedUserData.userId, roleId: parsedUserData.roleId })).subscribe((response: IApplicationResponse) => {
                    if (response.isSuccess && response.statusCode == 200) {
                      this.store.dispatch(new SidebarDataLoaded({ response: response.resources }));
                      this.router.navigateByUrl(DEFAULT_COMMON_DASHBOARD_NAVIGATION);
                    }
                    else {
                      this.store.dispatch(new Logout());
                      this.store.dispatch(new SidebarDataRemoved());
                      this.onAfterSubmit();
                    }
                    this.isBtnDisabled = false;
                  }, error => {
                    this.onAfterSubmit();
                  });

                this.getUpdatedNotifications(parsedUserData.userId);
                if (parsedUserData.roleName == 'Technical Area Manager') {
                  this.getTAMFollowUpNotification();
                }
                this.hubConnectionForSalesAPIConfigs(parsedUserData.userId);
                this.getForkJoinRequests();
              }, 100);
            }
            else {
              this.onAfterSubmit();
              this.store.dispatch(new Logout());
              this.store.dispatch(new SidebarDataRemoved());
              this.loginForm?.reset();
            }
          })


        }
        else if (!response?.isSuccess && response?.statusCode == 500 && response.message === 'Invalid username or password') {
          this.remainingAttempts = response.resources?.remainingAttempts;
          this.isFormSubmitted = false;
          this.isBtnDisabled = false;
        }
        else {
          this.onAfterSubmit();
          //this.loginForm?.reset();
          //this.isUserTypeSelectedAsExternal = null;
          this.isBtnDisabled = false;
        }
      }, error => {
        this.onAfterSubmit();
        this.isBtnDisabled = false;
      });
  }

  onCheckWhetherUserLoggedInAnotherDevice(userId: string) {
    const deviceId = this.userService.deviceId ? this.userService.deviceId : CryptoUtils.createNewGuid().toString();
    const deviceInfo = this.deviceService.getDeviceInfo();
    this.userService.deviceId = deviceId;
    this.crudService.create(ModulesBasedApiSuffix.IT_MANAGEMENT, UserModuleApiSuffixModels.USER_DEVICE, {
      userId, deviceId,
      brand: deviceInfo.browser, manufacturer: deviceInfo.browser, appVersion: deviceInfo.browser_version, model: deviceInfo.os,
      operatingSystem: deviceInfo.os, operatingSystemVersion: deviceInfo.os_version
    })
      .subscribe(data => {
        if (data?.isSuccess && data?.statusCode == 200) {
          let deviceId = data?.resources;
          localStorage.setItem('dId', deviceId ? deviceId : '');
          // this.store.dispatch(new LoginUpdate({ deviceId }));
          // this.store.dispatch(new Login({ deviceId }));
        }
      });
  }

  prepareGlobalDateFormat(parsedUserData) {
    let parsedGlobalDateFormat = '';
    if (parsedUserData.dateFormat) {
      parsedGlobalDateFormat = parsedUserData.dateFormat.substr(0, parsedUserData.dateFormat.indexOf(' '));
    }
    // If a fresh login then default date format is hardcoded here
    else {
      parsedUserData.dateFormat = 'yyyy-mm-dd HH:mm:ss';
      parsedGlobalDateFormat = 'yyyy-m-dd';
    }
    localStorage.setItem('globalDateFormat', parsedGlobalDateFormat);
  }

  onSubmitSuccessLogic(response: any) {
    if (response.resources && response.resources['accessToken'] && response.resources['refreshToken'] && response.resources['expiresIn']) {
      this.aesGcmCryptoService.decrypt(response.resources['accessToken']).then((decryptedEncodedToken) => {
        const decodedUserData = this.userService.getDecodedLoggedInData(decryptedEncodedToken);
        if (decodedUserData) {
          const redirectRouteUrl = "common-dashboard";
          const parsedUserData = JSON.parse(decodedUserData['userData']);
          this.prepareGlobalDateFormat(parsedUserData);
          this.aesGcmCryptoService.encrypt(parsedUserData.userId).then((encryptedUserId: string) => {
            this.store.dispatch(new Login({
              user: parsedUserData, encodedToken: response.resources['accessToken'],
              encryptedUserId, defaultRedirectRouteUrl: redirectRouteUrl,
              refreshToken: response.resources['refreshToken']
            }));
            this.onCheckWhetherUserLoggedInAnotherDevice(parsedUserData.userId);
            this.crudService.get(ModulesBasedApiSuffix.IT_MANAGEMENT, UserModuleApiSuffixModels.MENU_ROLE_V1,
              undefined, false, prepareRequiredHttpParams({ userId: parsedUserData.userId, roleId: parsedUserData.roleId }))
              .subscribe((response1: IApplicationResponse) => {
                if (response1?.isSuccess && response1?.statusCode == 200) {
                  const decodedUserAccessPermData = response1.resources;
                  this.store.dispatch(new SidebarDataLoaded({ response: decodedUserAccessPermData }));
                  this.router.navigateByUrl('common-dashboard');
                }
                else {
                  this.store.dispatch(new Logout());
                  this.store.dispatch(new SidebarDataRemoved());
                  this.onAfterSubmit();
                }
                this.rxjsService.setGlobalLoaderProperty(false);
              }, error => {
                this.onAfterSubmit();
              });
            this.getUpdatedNotifications(parsedUserData.userId);
            if (parsedUserData.roleName == 'Technical Area Manager') {
              this.getTAMFollowUpNotification();
            }
            this.hubConnectionForSalesAPIConfigs(parsedUserData.userId);
            this.getForkJoinRequests();
          });
        }
        else {
          this.store.dispatch(new Logout());
          this.store.dispatch(new SidebarDataRemoved());
        }
      });
    }
  }

  onAfterSubmit() {
    this.rxjsService.setDialogOpenProperty(false);
    this.isFormSubmitted = false;
    this.rxjsService.setGlobalLoaderProperty(false);
  }

  versionDetails() {
    // this.router.navigate(['/version-details']);
    window.open("/version-details", "_blank");

    // this.crudService.dropdown(ModulesBasedApiSuffix.SHARED,
    //   SharedModuleApiSuffixModels.VERSIONS)
    //   .subscribe((response: IApplicationResponse) => {
    //       console.log('version response', response);
    //     if (response.isSuccess && response.statusCode == 200) {
    //       // this.versionsList = response.resources;
    //     }
    //   });
  }

  ngOnDestroy() {
    this.onAfterSubmit();
    this.rxjsService.setLoginSuccess(null);
  }
}